import { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $usersAccountStore } from '@lib/store';
import { PermissionUtils } from '@shared/utils';
import { UserType } from '@shared/api';
import { ManagerList } from './managerList';
import { AddManagerModal } from './addManagersModal';
import { RemoveManagerModal } from './removeManagerModal';
import { View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface ManagersProps {
  accountId: number;
}

export const Managers = (props: ManagersProps) => {
  const managersStore = useUnit($usersAccountStore.store);

  const [showAddMangerModal, setShowAddMangerModal] = useState<boolean>(false);
  const [showRemoveManagerModal, setShowRemoveManagerModal] = useState<boolean>(false);

  const { t } = useTranslation('account');

  const fetchManagers = useCallback(() => {
    $usersAccountStore.events.getAccountUsers({ UserType: UserType.Manager, AssignedAccountId: props.accountId });
    $usersAccountStore.events.getAllUsers({ UserType: UserType.Manager });
  }, [props.accountId]);

  useEffect(() => {
    fetchManagers();
    return () => $usersAccountStore.events.clear();
  }, [fetchManagers]);

  const closeAddModal = (confirm?: boolean) => {
    setShowAddMangerModal(false);
    if (confirm) fetchManagers();
  };
  const closeRemoveModal = (confirm?: boolean) => {
    setShowRemoveManagerModal(false);
    if (confirm) fetchManagers();
  };

  const unassignedManagers = useMemo(
    () => managersStore.allUsers.filter((user) => !managersStore.data.some((manager) => manager.id === user.id)),
    [managersStore.allUsers, managersStore.data],
  );
  return (
    <>
      <div className='flex flex-col gap-6'>
        <div className='flex w-full items-center justify-between overflow-x-auto'>
          <Text weight='semibold' size='2xl'>
            {t('managers.title')}
          </Text>
          {PermissionUtils.manageAccountsPermissions() && (
            <div className='flex gap-2'>
              <Button color='outlined' onClick={() => setShowAddMangerModal(true)}>
                {t('buttons.add', { ns: 'common' })}
              </Button>
              <Button color='outlined' disabled={!managersStore.data.length} onClick={() => setShowRemoveManagerModal(true)}>
                {t('buttons.remove', { ns: 'common' })}
              </Button>
            </div>
          )}
        </div>
        <View loading={managersStore.loading} error={managersStore.error} onRefresh={fetchManagers} noData={!managersStore.data.length}>
          <ManagerList data={managersStore.data} />
        </View>
      </div>
      <AddManagerModal isOpen={showAddMangerModal} onClose={closeAddModal} accountId={props.accountId} data={unassignedManagers} />
      <RemoveManagerModal isOpen={showRemoveManagerModal} onClose={closeRemoveModal} accountId={props.accountId} data={managersStore.data} />
    </>
  );
};
